import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from './AuthContext';

const MessageContext = createContext(null);

export const MessageProvider = ({ children }) => {
    const { user } = useAuth();
    const [unreadCount, setUnreadCount] = useState(0);
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchConversations = useCallback(async () => {
        if (!user) return;
        
        try {
            const response = await api.get('/messages/conversations');
            const data = response.data;
            setConversations(data);
            
            // Calculate total unread count
            const totalUnread = data.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
            setUnreadCount(totalUnread);
        } catch (err) {
            console.error('Failed to fetch conversations in context:', err);
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            setLoading(true);
            fetchConversations().finally(() => setLoading(false));
            
            // Poll for new messages every 30 seconds
            const interval = setInterval(fetchConversations, 30000);
            return () => clearInterval(interval);
        } else {
            setUnreadCount(0);
            setConversations([]);
        }
    }, [user, fetchConversations]);

    const markAsRead = async (messageId) => {
        try {
            await api.post(`/messages/mark-read/${messageId}`);
            fetchConversations(); // Refresh counts
        } catch (err) {
            console.error('Failed to mark message as read:', err);
        }
    };

    return (
        <MessageContext.Provider value={{ 
            unreadCount, 
            conversations, 
            fetchConversations, 
            markAsRead,
            loading 
        }}>
            {children}
        </MessageContext.Provider>
    );
};

export const useMessages = () => useContext(MessageContext);
